import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ImpersonationBanner from "@/components/ImpersonationBanner";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import RegisterPlaquePage from "@/pages/RegisterPlaquePage";
import RegisterFollowPlaquePage from "@/pages/RegisterFollowPlaquePage";
import PlaqueDetailPage from "@/pages/PlaqueDetailPage";
import AdminDashboard from "@/pages/AdminDashboard";
import PublicRatingPage from "@/pages/PublicRatingPage";
import PublicFollowPage from "@/pages/PublicFollowPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import FeedbackManagementPage from "@/pages/FeedbackManagementPage";

const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "https://www.reviewmybusiness.ie";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isImpersonating } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && (isImpersonating || !user.is_admin)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Redirect to external website
const ExternalRedirect = () => {
  window.location.href = WEBSITE_URL;
  return null;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ImpersonationBanner />
        <Routes>
          {/* Redirect home to external website */}
          <Route path="/" element={<ExternalRedirect />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Public Routes */}
          <Route path="/r/:code" element={<PublicRatingPage />} />
          <Route path="/f/:code" element={<PublicFollowPage />} />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <AccountSettingsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/register-plaque" 
            element={
              <ProtectedRoute>
                <RegisterPlaquePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/register-follow" 
            element={
              <ProtectedRoute>
                <RegisterFollowPlaquePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/plaque/:id" 
            element={
              <ProtectedRoute>
                <PlaqueDetailPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/analytics" 
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/feedback" 
            element={
              <ProtectedRoute>
                <FeedbackManagementPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin Routes */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Catch all - redirect to external website */}
          <Route path="*" element={<ExternalRedirect />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
